import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Input, Select, message, Radio } from 'antd';
import { base_url } from '../utils/baseUrl';
import axios from 'axios';
import { config } from '../utils/axiosconfig';

const { Option } = Select;

const MailchimpCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [templateType, setTemplateType] = useState('custom'); // 'custom', 'welcome', 'product_launch', 'campaign'

  // Fetch campaigns from backend
  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      // Include authentication token in the request
      const response = await axios.get(`${base_url}mailchimp/campaigns`, config);
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      message.error('Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Handle form submission for creating a new campaign
  const onFinish = async (values) => {
    try {
      // Prepare data based on template type
      const campaignData = {
        ...values,
        template_type: templateType
      };

      // Include authentication token in the request
      const response = await axios.post(`${base_url}mailchimp/campaigns`, campaignData, config);
      if (response.data.success) {
        message.success(response.data.message || 'Campaign created successfully');
        form.resetFields();
        fetchCampaigns(); // Refresh the list
      } else {
        message.error(response.data.message || 'Failed to create campaign');
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create campaign';
      message.error(errorMessage);
    }
  };

  const columns = [
    {
      title: 'Campaign Name',
      dataIndex: 'settings',
      key: 'name',
      render: (settings) => settings?.title || 'N/A'
    },
    {
      title: 'Subject',
      dataIndex: 'settings',
      key: 'subject',
      render: (settings) => settings?.subject_line || 'N/A'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Sent Date',
      dataIndex: 'send_time',
      key: 'send_time',
      render: (text) => text || 'Not sent',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <span>
          <Button type="link" onClick={() => handleViewCampaign(record.id)}>View</Button>
          {record.status === 'save' && (
            <>
              <Button type="link" onClick={() => handleSendCampaign(record.id)}>Send</Button>
              <Button type="link" danger onClick={() => handleDeleteCampaign(record.id)}>Delete</Button>
            </>
          )}
        </span>
      ),
    },
  ];

  const handleViewCampaign = (campaignId) => {
    // Implement view campaign logic
    message.info(`View campaign ${campaignId}`);
  };

  const handleSendCampaign = async (campaignId) => {
    try {
      // Include authentication token in the request
      await axios.post(`${base_url}mailchimp/campaigns/${campaignId}/send`, {}, config);
      message.success('Campaign sent successfully');
      fetchCampaigns(); // Refresh the list
    } catch (error) {
      console.error('Error sending campaign:', error);
      message.error('Failed to send campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      // Include authentication token in the request
      await axios.delete(`${base_url}mailchimp/campaigns/${campaignId}`, config);
      message.success('Campaign deleted successfully');
      fetchCampaigns(); // Refresh the list
    } catch (error) {
      console.error('Error deleting campaign:', error);
      message.error('Failed to delete campaign');
    }
  };

  return (
    <div>
      <h3 className="mb-4 title">Mailchimp Campaign Management</h3>
      
      <div className="mb-4">
        <h4>Create New Campaign</h4>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Form.Item
            name="name"
            label="Campaign Name"
            rules={[{ required: true, message: 'Please input campaign name!' }]}
          >
            <Input placeholder="Enter campaign name" />
          </Form.Item>
          
          <Form.Item
            name="subject"
            label="Email Subject"
            rules={[{ required: true, message: 'Please input email subject!' }]}
          >
            <Input placeholder="Enter email subject" />
          </Form.Item>
          
          <Form.Item
            name="template_type"
            label="Email Template"
          >
            <Radio.Group 
              onChange={(e) => setTemplateType(e.target.value)} 
              value={templateType}
            >
              <Radio value="custom">Custom Content</Radio>
              <Radio value="welcome">Welcome Email</Radio>
              <Radio value="product_launch">Product Launch</Radio>
              <Radio value="campaign">Campaign Template</Radio>
            </Radio.Group>
          </Form.Item>
          
          {templateType === 'custom' && (
            <Form.Item
              name="content"
              label="Email Content"
              rules={[{ required: true, message: 'Please input email content!' }]}
            >
              <Input.TextArea placeholder="Enter email content" rows={6} />
            </Form.Item>
          )}
          
          <Form.Item
            name="list_id"
            label="Audience List"
            rules={[{ required: true, message: 'Please select audience list!' }]}
          >
            <Select placeholder="Select audience list">
              <Option value="6a636fa872">Main Audience List</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Create Campaign
            </Button>
          </Form.Item>
        </Form>
      </div>
      
      <div>
        <h4>Existing Campaigns</h4>
        <Table
          dataSource={campaigns}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </div>
    </div>
  );
};

export default MailchimpCampaigns;